<template>
  <section v-if="isVisible" class="demo-access">
    <div class="demo-access__header">
      <div>
        <p class="demo-access__eyebrow">Demo Access</p>
        <h2 class="demo-access__title">Explore HarborGate personas</h2>
      </div>
      <p class="demo-access__copy">
        Pick a seeded persona to sign in through the demo-auth backend.
      </p>
    </div>

    <div v-if="loading" class="demo-access__state">
      <b-loading :active="true" :is-full-page="false" />
    </div>

    <div v-else-if="rootNode" class="org-tree">
      <OrgTreeNode
        :node="rootNode"
        :pending-email="pendingEmail"
        :submitting="submitting"
        @select="handlePersonaSelect"
      />
    </div>

    <div v-if="requiresDemoKey" class="demo-access__keybox">
      <b-field
        label="Demo Key"
        :type="demoKeyError ? 'is-danger' : null"
        :message="demoKeyError || 'This backend requires a shared demo key before persona login can continue.'"
      >
        <b-input
          v-model="demoKey"
          password-reveal
          type="password"
          placeholder="Enter demo key"
          :disabled="submitting"
          @keyup.enter="retryWithDemoKey"
        />
      </b-field>

      <div class="demo-access__actions">
        <b-button
          type="is-primary"
          :loading="submitting"
          :disabled="!pendingEmail || !demoKey"
          @click="retryWithDemoKey"
        >
          Continue Demo Login
        </b-button>
      </div>
    </div>

    <p v-if="errorMessage" class="demo-access__error">{{ errorMessage }}</p>
  </section>
</template>

<script>
import { demoLogin, getDemoUsers } from '@/utils/helpers'
import OrgTreeNode from '@/components/auth/OrgTreeNode.vue'

const DEMO_ORG_CHART = [
  {
    key: 'evan-carver',
    managerKey: null,
    directReports: ['jordan-meyers', 'elise-donovan', 'riley-thompson', 'casey-whitman'],
  },
  {
    key: 'jordan-meyers',
    managerKey: 'evan-carver',
    directReports: ['rachel-kim', 'samir-patel', 'dylan-brooks', 'priya-shah', 'jenna-carlisle', 'alex-mercer', 'tori-nguyen'],
  },
  {
    key: 'rachel-kim',
    managerKey: 'jordan-meyers',
    directReports: ['mateo-alvarez', 'thomas-rivera'],
  },
  { key: 'mateo-alvarez', managerKey: 'rachel-kim', directReports: [] },
  { key: 'thomas-rivera', managerKey: 'rachel-kim', directReports: [] },
  { key: 'samir-patel', managerKey: 'jordan-meyers', directReports: [] },
  { key: 'dylan-brooks', managerKey: 'jordan-meyers', directReports: [] },
  { key: 'priya-shah', managerKey: 'jordan-meyers', directReports: [] },
  { key: 'jenna-carlisle', managerKey: 'jordan-meyers', directReports: [] },
  { key: 'alex-mercer', managerKey: 'jordan-meyers', directReports: [] },
  { key: 'tori-nguyen', managerKey: 'jordan-meyers', directReports: [] },
  { key: 'elise-donovan', managerKey: 'evan-carver', directReports: [] },
  { key: 'riley-thompson', managerKey: 'evan-carver', directReports: [] },
  { key: 'casey-whitman', managerKey: 'evan-carver', directReports: [] },
]

export default {
  name: 'DemoAccessPanel',
  components: {
    OrgTreeNode,
  },
  props: {
    initialDemoKey: {
      type: String,
      default: '',
    },
  },
  emits: ['authenticated', 'demo-key-updated'],
  data() {
    return {
      users: [],
      loading: false,
      submitting: false,
      unavailable: false,
      pendingEmail: '',
      requiresDemoKey: false,
      demoKey: this.initialDemoKey,
      demoKeyError: '',
      errorMessage: '',
    }
  },
  computed: {
    isVisible() {
      return !this.unavailable
    },
    orgByKey() {
      return DEMO_ORG_CHART.reduce((accumulator, person) => {
        accumulator[person.key] = person
        return accumulator
      }, {})
    },
    personaByKey() {
      return this.users.reduce((accumulator, persona) => {
        const emailKey = (persona.email || '').split('@')[0].toLowerCase().replace(/\./g, '-')
        const nameKey = [persona.firstName, persona.lastName]
          .filter(Boolean)
          .join('-')
          .toLowerCase()
          .replace(/\s+/g, '-')

        if (emailKey) accumulator[emailKey] = persona
        if (nameKey) accumulator[nameKey] = persona
        return accumulator
      }, {})
    },
    rootNode() {
      return this.buildTree('evan-carver')
    },
  },
  async mounted() {
    await this.loadDemoUsers()
  },
  watch: {
    initialDemoKey(value) {
      this.demoKey = String(value || '')
    },
    demoKey: {
      immediate: true,
      handler(value) {
        this.$emit('demo-key-updated', value)
      },
    },
  },
  methods: {
    buildTree(key) {
      const orgPerson = this.orgByKey[key]
      const persona = this.personaByKey[key]

      if (!orgPerson || !persona) return null

      return {
        key,
        managerKey: orgPerson.managerKey,
        persona,
        children: orgPerson.directReports
          .map((childKey) => this.buildTree(childKey))
          .filter(Boolean),
      }
    },
    async loadDemoUsers() {
      this.loading = true
      this.errorMessage = ''

      try {
        const users = await getDemoUsers()
        this.users = Array.isArray(users) ? users : []

        if (!this.users.length) {
          this.unavailable = true
        }
      } catch (error) {
        if (error?.response?.status === 404) {
          this.unavailable = true
          return
        }

        this.errorMessage = 'Demo access is unavailable right now.'
      } finally {
        this.loading = false
      }
    },
    async handlePersonaSelect(persona) {
      this.pendingEmail = persona.email
      this.errorMessage = ''
      this.demoKeyError = ''

      await this.submitDemoLogin(persona.email, this.demoKey)
    },
    async retryWithDemoKey() {
      this.errorMessage = ''
      this.demoKeyError = ''

      if (!this.pendingEmail) return

      await this.submitDemoLogin(this.pendingEmail, this.demoKey)
    },
    async submitDemoLogin(email, demoKey) {
      this.submitting = true

      try {
        const user = await demoLogin(email, demoKey)
        this.requiresDemoKey = false
        this.demoKeyError = ''
        this.$emit('demo-key-updated', demoKey)
        this.$emit('authenticated', user)
      } catch (error) {
        const status = error?.response?.status
        const message = error?.response?.data?.message

        if (status === 401) {
          this.requiresDemoKey = true
          this.demoKeyError = demoKey
            ? 'That demo key was rejected. Check the key and try again.'
            : ''
          this.errorMessage = demoKey
            ? ''
            : 'A demo key is required for this environment.'
          return
        }

        this.errorMessage = message || 'Demo login failed. Please try again.'
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.demo-access {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid $color-border;
  text-align: left;
}

.demo-access__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
}

.demo-access__eyebrow {
  margin: 0 0 0.35rem;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $color-primary-dark;
}

.demo-access__title {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 800;
  color: $color-text;
}

.demo-access__copy {
  margin: 0;
  max-width: 18rem;
  font-size: 0.95rem;
  line-height: 1.5;
  color: $color-text-muted;
}

.demo-access__state {
  position: relative;
  min-height: 5rem;
}

.org-tree {
  max-width: 920px;
  padding-bottom: 0.5rem;
  margin: 0 auto;
}

.demo-access__keybox {
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid $color-border;
  border-radius: $radius-lg;
  background: $color-surface-soft;
}

.demo-access__actions {
  display: flex;
  justify-content: flex-end;
}

.demo-access__error {
  margin: 0.9rem 0 0;
  color: $color-danger;
  font-size: 0.92rem;
}

@media (max-width: 768px) {
  .demo-access__header {
    flex-direction: column;
  }

  .demo-access__copy {
    max-width: none;
  }
}
</style>
